import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { RecordingButton } from '../components/RecordingButton';
import { StreakDisplay } from '../components/StreakDisplay';
import { FocusAreaSelector } from '../components/FocusAreaSelector';
import { GenerateModal } from '../components/GenerateModal';

export function Dashboard() {
  const { member } = useAuth();
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const [selectedFocusArea, setSelectedFocusArea] = useState<string>('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  // Listen for phrase updates (e.g., when language changes)
  useEffect(() => {
    const handlePhraseUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['todaysPhrase'] });
    };
    window.addEventListener('phrase-updated', handlePhraseUpdate);
    return () => window.removeEventListener('phrase-updated', handlePhraseUpdate);
  }, [queryClient]);

  const { data: todayData, isLoading: loadingToday } = useQuery({
    queryKey: ['todaysPhrase'],
    queryFn: () => api.getTodaysPhrase()
  });

  const { data: streakData } = useQuery({
    queryKey: ['myStreak'],
    queryFn: () => api.getMyStreak()
  });

  const { data: statsData } = useQuery({
    queryKey: ['familyStats'],
    queryFn: () => api.getFamilyStats()
  });

  const generateMutation = useMutation({
    mutationFn: (focusArea: string) => api.generatePhrase(focusArea, undefined, true, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todaysPhrase'] });
    }
  });

  const refreshMutation = useMutation({
    mutationFn: async () => {
      // Generate a new phrase in the current language
      const newPhrase = await api.generatePhrase(undefined, undefined, true, language);
      // Assign it as today's phrase for the current member
      if (member?.id && newPhrase?.id) {
        await api.assignPhrase(member.id, undefined, newPhrase.id);
      }
      return newPhrase;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['todaysPhrase'] });
    }
  });

  const assignment = todayData?.assignment;
  const phraseContent = assignment?.phrase_content || assignment?.custom_phrase_content;
  const streak = streakData?.streak;

  const handleRecordingComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['myStreak'] });
    queryClient.invalidateQueries({ queryKey: ['todaysPhrase'] });
    queryClient.invalidateQueries({ queryKey: ['familyStats'] });
  };

  const handleGenerateNew = () => {
    generateMutation.mutate(selectedFocusArea || 'growth');
  };

  const handleRefreshAffirmation = () => {
    refreshMutation.mutate();
  };

  return (
    <div className="container py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 fade-in">
        <div>
          <h1 className="text-2xl mb-1">{getGreeting(t)}, {member?.name || 'Friend'}!</h1>
          <p className="text-gray-600">{t('readyForToday')}</p>
        </div>
        <StreakDisplay
          currentStreak={streak?.current_streak || 0}
          longestStreak={streak?.longest_streak || 0}
        />
      </div>

      {/* Family Stats */}
      {statsData && (
        <div className="grid grid-4 mb-8 fade-in" style={{ animationDelay: '0.1s' }}>
          <StatCard
            icon="👨‍👩‍👧‍👦"
            value={statsData.totalMembers}
            label={t('familyMembers')}
          />
          <StatCard
            icon="✅"
            value={`${statsData.completedToday}/${statsData.totalMembers}`}
            label={t('doneToday')}
          />
          <StatCard
            icon="🎙️"
            value={statsData.totalRecordings}
            label={t('totalRecordings')}
          />
          <StatCard
            icon="👑"
            value={statsData.allTimeBestStreak}
            label={t('bestStreak')}
          />
        </div>
      )}

      {/* Today's Affirmation */}
      <div className="card card-large mb-8 fade-in" style={{ animationDelay: '0.2s' }}>
        <div className="text-center">
          <span className="inline-block px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-semibold mb-6">
            {t('todaysAffirmation')}
          </span>

          {loadingToday ? (
            <div className="py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            </div>
          ) : phraseContent ? (
            <>
              <p className="affirmation-text">"{phraseContent}"</p>

              {/* Subtle refresh button */}
              <button
                onClick={handleRefreshAffirmation}
                disabled={refreshMutation.isPending}
                className="refresh-affirmation-btn"
                title={t('changeAffirmation')}
              >
                {refreshMutation.isPending ? (
                  <span className="animate-spin">↻</span>
                ) : (
                  <span>↻</span>
                )}
                <span className="refresh-text">{refreshMutation.isPending ? t('refreshing') : t('changeAffirmation')}</span>
              </button>

              {assignment && !assignment.completed && (
                <div className="mt-8">
                  <p className="text-gray-600 mb-4">
                    {t('recordPrompt')}
                  </p>
                  <RecordingButton
                    phraseId={assignment.phrase_id || ''}
                    assignmentId={assignment.id}
                    onComplete={handleRecordingComplete}
                  />
                </div>
              )}

              {!!assignment?.completed && (
                <div className="mt-6 p-4 bg-green-50 rounded-xl">
                  <span className="text-2xl">🎉</span>
                  <p className="text-green-700 font-semibold mt-2">
                    {t('greatJob')}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="py-8">
              <p className="text-gray-600 mb-6">
                {t('generatePrompt')}
              </p>
              <FocusAreaSelector
                selected={selectedFocusArea}
                onSelect={setSelectedFocusArea}
              />
              <button
                className="btn btn-primary btn-large mt-6"
                onClick={handleGenerateNew}
                disabled={generateMutation.isPending}
              >
                {generateMutation.isPending ? t('generating') : `✨ ${t('generateButton')}`}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-3 fade-in" style={{ animationDelay: '0.3s' }}>
        <ActionCard
          icon="📚"
          title={t('browseLibrary')}
          description={t('browseLibraryDesc')}
          link="/library"
        />
        <ActionCard
          icon="🎯"
          title={t('generateNew')}
          description={t('generateNewDesc')}
          onClick={() => setShowGenerateModal(true)}
        />
        <ActionCard
          icon="🎧"
          title={t('listenBack')}
          description={t('listenBackDesc')}
          link="/recordings"
        />
      </div>

      {/* Generate Modal */}
      <GenerateModal
        isOpen={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
      />
    </div>
  );
}

function StatCard({ icon, value, label }: { icon: string; value: number | string; label: string }) {
  return (
    <div className="card text-center" style={{ padding: '1.25rem' }}>
      <span style={{ fontSize: '1.75rem', display: 'block' }}>{icon}</span>
      <p className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--primary)', marginTop: '0.5rem' }}>{value}</p>
      <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', marginTop: '0.25rem' }}>{label}</p>
    </div>
  );
}

function ActionCard({ icon, title, description, onClick, link }: {
  icon: string;
  title: string;
  description: string;
  onClick?: () => void;
  link?: string;
}) {
  const content = (
    <div className="card hover:shadow-lg transition-shadow cursor-pointer h-full" style={{ padding: '1.5rem' }}>
      <span style={{ fontSize: '2rem', display: 'block', marginBottom: '0.75rem' }}>{icon}</span>
      <h3 className="font-semibold" style={{ marginBottom: '0.5rem' }}>{title}</h3>
      <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem' }}>{description}</p>
    </div>
  );

  if (link) {
    return <a href={link} className="no-underline">{content}</a>;
  }

  return <div onClick={onClick}>{content}</div>;
}

function getGreeting(t: (key: 'goodMorning' | 'goodAfternoon' | 'goodEvening') => string): string {
  const hour = new Date().getHours();
  if (hour < 12) return t('goodMorning');
  if (hour < 17) return t('goodAfternoon');
  return t('goodEvening');
}
